import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { OrganizationalImpactWebComponent } from '../../organizationalImpact';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { IncidentsSideMenuWebComponent } from '../../../edr/incidentsSideMenu';
import { processEntitiesRowData, processResourcesRowData } from '../../../utils/xdr.utils';
import { WEB_COMPONENT_ORGANIZATIONAL_IMPACT } from '../../../webcomponents.constants';
import { IncidentDataOrganizationalImpact } from '../types';
import { PanelType } from '../../../edr/incidentsSideMenu/types';
import { ITEM_CLICKED } from '../../organizationalImpact/constants';
import { TemplateState } from '../../../types/common/types';

export class XdrOverviewView extends BaseWebComponentView {
  private incidentDataForOrganizationalImpact: IncidentDataOrganizationalImpact;
  private incidentsSideMenuStrings;
  private resizeObserver: ResizeObserver;

  getDomElements() {
    this.dom.$xdrOverviewContent = this.shadow.querySelector('.xdr-overview-content');
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$summary = this.shadow.querySelector('gz-summary-panel');
    this.dom.$tacticsAndTechniques = this.shadow.querySelector('gz-tactics-and-techniques');
    this.dom.$suspectedActorsCard = this.shadow.querySelector('gz-suspected-actors-card-component');
    this.dom.$timeline = this.shadow.querySelector('gz-timeline-group');
    this.dom.$organizationalImpactCard = this.shadow.querySelector('#organizationImpactCard');
    this.dom.$openUnknownSidePanel = this.shadow.querySelector('#open-unknown-side-panel');
    this.dom.$responsePanel = this.shadow.querySelector('gz-response-panel');
    this.dom.$riskIncidentPanel = this.shadow.querySelector('gz-risk-incident-panel');
    this.dom.$divRiskPanel = this.shadow.querySelector('#risk-incident-panel');
    this.dom.$highlightsTitle = this.shadow.querySelector('#highlights-title');

    this.dom.$summary.injectDependencies(this.dependencies);
    this.dom.$timeline.injectDependencies(this.dependencies);
    this.dom.$tacticsAndTechniques.injectDependencies(this.dependencies);
    this.dom.$responsePanel.injectDependencies(this.dependencies);
    this.dom.$riskIncidentPanel.injectDependencies(this.dependencies);

    this.dom.$sideMenu = this.shadow.querySelector('#sideMenu') as GzSideMenuWebComponent;
    this.dom.$incidentOverviewSideMenu = this.shadow.querySelector(
      '#incidentOverviewSideMenu'
    ) as IncidentsSideMenuWebComponent;

    this.setTranslatedStrings();
  }

  getTranslations() {
    return {
      incidentsSideMenuStrings: this.incidentsSideMenuStrings,
    };
  }

  setTranslatedStrings() {
    this.incidentsSideMenuStrings = this.stringsTranslator.getIncidentsSideMenuStrings();
    this.dom.$highlightsTitle.innerHTML = this.dependencies.translateFn('Highlights');
  }

  buildSuccessView(data: any) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setupIncidentComponents(data);
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private setupIncidentComponents(incident) {
    this.setupIncidentSummary(incident);
    this.setupIncidentTimeline(incident);
    this.setupIncidentMitre(incident);
    this.setupIncidentSuspectedActors(incident);
    this.setupIncidentResponsePanel(incident);
    this.setupRiskIncidentPanel(incident);
    this.setupOrganizationalImpact(incident);
  }

  private setupIncidentSummary(incident) {
    this.dom.$summary.incidentId = incident.incidentId;
    this.dom.$summary.incidentScore = incident.score;
    this.dom.$summary.created = incident.created;
    this.dom.$summary.lastUpdated = incident.lastUpdated;
    this.dom.$summary.attackTypes = incident.attackTypes;
    this.dom.$summary.narrative = incident.narrative;
    this.dom.$summary.reasoning = incident.reasoning;
    this.dom.$summary.summaryMonitoring = incident.summaryMonitoring;
    this.dom.$summary.incidentCompanyName = incident.company.name ?? incident.incidentCompanyData.name;
    this.dom.$summary.incidentNumber = incident.number;
    this.dom.$summary.start();
  }

  private setupIncidentTimeline(incident) {
    this.dom.$timeline.alertsData = incident.alertsData;
    this.dom.$timeline.start();
  }

  private setupIncidentMitre(incident) {
    this.dom.$tacticsAndTechniques.mitreTags = incident.mitreTags;
    this.dom.$tacticsAndTechniques.start();
  }

  private setupIncidentSuspectedActors(incident) {
    this.dom.$suspectedActorsCard.attribution = incident.attribution;
    this.dom.$suspectedActorsCard.errorState = incident.attribution?.actors.errorState ?? undefined;
    this.dom.$suspectedActorsCard.companyId = incident.company._id;
  }

  private setupIncidentResponsePanel(incident) {
    this.dom.$responsePanel.responseData = incident.responseData;
    this.dom.$responsePanel.incidentId = incident.incidentId;
    this.dom.$responsePanel.start();
  }

  private setupRiskIncidentPanel(incident) {
    this.dom.$riskIncidentPanel.incidentId = incident.incidentId;
    this.dom.$riskIncidentPanel.start();
    if (this.dom.$riskIncidentPanel) {
      this.resizeObserver = new ResizeObserver(() => {
        this.dom.$riskIncidentPanel.controller.view.setMandatoryPropertiesDonut();
      });
      this.resizeObserver.observe(this.dom.$divRiskPanel);
    }
  }

  private setupOrganizationalImpact(incident) {
    const organizationalImpact = new OrganizationalImpactWebComponent();

    organizationalImpact.sectionTitle = this.dependencies.translateFn('Organization impact');
    organizationalImpact.entitiesTitle = this.dependencies.translateFn('Entities');
    organizationalImpact.resourcesTitle = this.dependencies.translateFn('Resources');
    organizationalImpact.entities = incident.entities;
    organizationalImpact.resources = incident.resources;

    this.incidentDataForOrganizationalImpact = {
      id: incident.incidentId,
      type: incident.type,
      number: incident.number,
      correlatedIncidents: incident.correlatedIncidents,
      isGzDemo: incident.isGzDemo,
    };

    this.dom.$organizationalImpactCard.appendChild(organizationalImpact);
  }

  public unobserveRiskPanel(): void {
    this.resizeObserver.unobserve(this.dom.$divRiskPanel);
  }

  handleEntitiesClick(event: CustomEvent) {
    const categories = processEntitiesRowData(
      {
        entities: event.detail.entities,
        _id: this.incidentDataForOrganizationalImpact.id,
        number: this.incidentDataForOrganizationalImpact.number,
        type: this.incidentDataForOrganizationalImpact.type,
        correlatedIncidents: this.incidentDataForOrganizationalImpact.correlatedIncidents,
        isGzDemo: this.incidentDataForOrganizationalImpact.isGzDemo,
      },
      Translator.translateXdrEntityType
    );

    const { title, searchCategoriesPlaceholder } = this.stringsTranslator.getEntitiesSidePanelStrings();
    this.openSideMenu(categories, event.detail.type, PanelType.ENTITIES_PANEL, title, searchCategoriesPlaceholder);
  }

  handleResourcesClick(event: CustomEvent) {
    const categories = processResourcesRowData(
      {
        resources: event.detail.resources,
        _id: this.incidentDataForOrganizationalImpact.id,
        number: this.incidentDataForOrganizationalImpact.number,
        type: this.incidentDataForOrganizationalImpact.type,
        correlatedIncidents: this.incidentDataForOrganizationalImpact.correlatedIncidents,
        isGzDemo: this.incidentDataForOrganizationalImpact.isGzDemo,
      },
      Translator.translateXdrResourceType
    );

    const { title, searchCategoriesPlaceholder } = this.stringsTranslator.getResourcesSidePanelStrings();
    this.openSideMenu(categories, event.detail.type, PanelType.RESOURCES_PANEL, title, searchCategoriesPlaceholder);
  }

  openSideMenu(categories, categoryToScroll, panelType, title, searchCategoriesPlaceholder) {
    const incidentsSideMenu = new IncidentsSideMenuWebComponent();

    incidentsSideMenu.categories = categories;
    incidentsSideMenu.categoryToScroll = categoryToScroll;
    incidentsSideMenu.title = title;
    incidentsSideMenu.panelType = panelType;
    incidentsSideMenu.searchCategoriesPlaceholder = searchCategoriesPlaceholder;
    incidentsSideMenu.translations = this.stringsTranslator.getIncidentsSideMenuStrings();
    incidentsSideMenu.incidentId = this.incidentDataForOrganizationalImpact.id;

    this.dom.$sideMenu.setMenuContent(incidentsSideMenu);
    this.dom.$sideMenu.openSideMenu();

    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(ITEM_CLICKED, {
        bubbles: true,
        composed: true,
      })
    );
  }

  handleSideMenuClose() {
    const organizationImpactComponent = this.dom.$organizationalImpactCard.querySelector(
      WEB_COMPONENT_ORGANIZATIONAL_IMPACT
    );
    organizationImpactComponent.removeActiveStateForIconsLabels();
    this.dom.$sideMenu.setMenuContent(null);
  }
}
